import Vue                  from "vue";
import vSelect              from "vue-select";
import { mapGetters }       from "vuex"
import { sortBy }           from "lodash";
import { TmsService, SmartEquipService } from "@/services/";
import { LoadingSpinner }   from "@/components";
import { DnRouterLink }     from "@/components/Elements";
import PartSearchFacets     from "@/components/SubMenus/PartSearchFacets.vue";
import { faChevronDown }    from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon }  from "@fortawesome/vue-fontawesome";

/**
 * PartSearchTruckSelector – Multi-step "wizard" for selecting a truck make,
 * model, and component to search parts for. Supports two flows:
 *
 *  1. **OEM / SmartEquip flow** – Make -> Model -> Component
 *     Group -> Component (produces a diagram search).
 *  2. **Competitive / TMS flow** – Make -> Model -> Category (produces a
 *     traditional parts list search).
 *
 * Can run as a standalone page or in `embedded` mode inside PartDiagramPage.
 */
export default Vue.extend({
    name: "PartSearchTruckSelector",
    props: {
        /** Pre-selected make (for deep-linking). */
        make: {
            type: String,
            default: null
        },
        /** Pre-selected model (for deep-linking). */
        model: {
            type: String,
            default: null
        },
        /** Pre-selected category (for deep-linking). */
        category: {
            type: String,
            default: null
        },
        /** Pre-selected component group (for deep-linking). */
        componentGroup: {
            type: String,
            default: null
        },
        /** Pre-selected component (for deep-linking). */
        component: {
            type: String,
            default: null
        },
        /** When true, renders as a compact sidebar panel inside PartDiagramPage. */
        embedded: {
            type: Boolean,
            default: false
        },
        /** Available facets from search results (used in embedded mode). */
        availableFacets: {
            type: Object,
            default: () => ({})
        },
        /** Currently selected facets (used in embedded mode). */
        externalSelectedFacets: {
            type: Object,
            default: () => ({})
        }
    },
    components: {
        LoadingSpinner,
        DnRouterLink,
        vSelect,
        PartSearchFacets,
        FontAwesomeIcon
    },
    data: () => ({
        // --- Loading flags ---
        loading: true,
        loadingModels: false,
        loadingCategories: false,
        loadingComponents: false,
        loadingComponentGroups: false,

        // --- Selections ---
        selectedMake: "" as any,
        selectedModel: "" as any,
        selectedComponent: null as any,
        selectedComponentGroup: "" as any,
        selectedCategory: "",

        // --- Option lists ---
        makes: [] as any[],
        models: [] as any[],
        componentGroups: [] as any[],
        displayedComponents: [] as any[],
        /** IDs of parent components whose children are currently expanded. */
        expandedComponentIds: [] as string[],
        categories: [] as any[],

        // --- Flags ---
        showDiagram: false,
        IsOemMake: false,

        // --- Facet-related (used only in non-embedded / standalone mode) ---
        selectedFacets: {} as Record<string, any>,
        searchTerm: "",
        isTms: true,

        // --- Icons ---
        chevronDownIcon: faChevronDown,
    }),
    computed: {
        ...mapGetters(["user", "audienceIds", "activeAccountId"]),

        /** Placeholder text for the Model dropdown. */
        ModelPlaceholder(): string {
            if (this.loadingModels) return "Loading models...";
            if (this.selectedMake && !this.loadingModels && !this.models.length) {
                return "No models found for this make.";
            }
            return this.selectedMake === "CLARK" ? "Select a Lot" : "Select a Model";
        },

        /** Placeholder text for the Component Group dropdown. */
        ComponentGroupPlaceholder(): string {
            if (this.loadingComponentGroups) return "Loading component groups...";
            if (this.selectedModel && !this.loadingComponentGroups && !this.componentGroups.length) {
                return "No component groups found for this model.";
            }
            return "Select a Component Group";
        },

        /** Placeholder text for the Category dropdown. */
        CategoryPlaceholder(): string {
            if (this.loadingCategories) return "Loading categories...";
            if (this.selectedMake && this.selectedModel && !this.loadingCategories && !this.categories.length) {
                return "No categories found for this model.";
            }
            return "Select a Category";
        }
    },
    watch: {
        /** React to make changes: determine flow (OEM vs competitive) and load next step. */
        selectedMake(newMake: any) {
            // Skip watcher side-effects during state restoration from query params
            if ((this as any)._isRestoring) return;

            this.IsOemMake = !!(newMake && newMake.BrandId && newMake.BrandId !== 0);
            this.showDiagram = this.IsOemMake;

            if (this.IsOemMake) {
                this.GetModelsSmartEquip();
            } else if (newMake) {
                this.GetCompetitiveModels();
            }

            this.updateQueryString();

            this.$emit("make-selected", { 
                IsOemMake: this.IsOemMake, 
                brandId: newMake ? newMake.BrandId : null,
                showDiagram: this.showDiagram
            });
        }
    },
    created() {   
        // If make and model props are provided, use GetData to restore state
        if (this.make && this.model) {
            this.GetData();
        } else {
            this.GetMakes();
        }

        // Listen for browser back/forward to reset selections when
        // query params are cleared. Query params are managed via
        // history.replaceState so $route does not update on popstate.
        if (this.embedded) {
            this._onPopState = () => {
                this.selectedMake = "";
                this.clearDependentSelections("model");
                this.showDiagram = false;
                this.IsOemMake = false;
            };
            window.addEventListener("popstate", this._onPopState);
        }
    },
    beforeDestroy() {
        if (this._onPopState) {
            window.removeEventListener("popstate", this._onPopState);
        }
    },
    methods: {
        // ─── Event forwarding ──────────────────────────────────────────

        /** Forward facet-update event to the parent. */
        UpdateFacets() {
            this.$emit("updateFacets");
        },

        /** Forward truck-selection redirect event to the parent. */
        redirectToTruckSelect() {
            this.$emit("truckSelectionRedirect");
        },

        // ─── Data fetching ─────────────────────────────────────────────

        /** Fetches the list of available makes (OEMs) from TmsService. */
        async GetMakes() {
            try {
                this.loading = true;
                const searchInput = this.CreateSearchInput();
                const response = await TmsService.GetOems(searchInput);
                this.makes = sortBy(response?.data ?? [], (m: any) => (m.OemDescription || "").toLowerCase());
                
                // Try to restore the selectedMake from the prop
                if (this.make && this.makes.length > 0) {
                    const matchedMake = this.makes.find((m: any) => 
                        m.OemDescription === this.make
                    );
                    if (matchedMake) {
                        this.selectedMake = matchedMake;
                    }
                }
            } catch (error) {
                console.error("Error loading makes:", error);
                this.makes = [];
            } finally {
                this.loading = false;
            }
        },

        /**
         * Bootstraps the selector when make and model are pre-selected
         * (e.g. from query string deep-link). Uses _isRestoring flag to
         * prevent the selectedMake watcher from firing side-effects.
         * Weird specific logic that the business requested instead of just changing the truck selection
         */
        async GetData() {
            (this as any)._isRestoring = true;
            try {
                this.loading = true;
                await this.GetMakes();
                
                if (this.selectedMake) {
                    // Determine if OEM or competitive based on make
                    this.IsOemMake = !!(this.selectedMake.BrandId && this.selectedMake.BrandId !== 0);
                    this.showDiagram = this.IsOemMake;
                    
                    // Emit make-selected so parent knows the flow type
                    this.$emit("make-selected", {
                        IsOemMake: this.IsOemMake,
                        brandId: this.selectedMake.BrandId || null,
                        showDiagram: this.showDiagram
                    });
                    
                    // Load models
                    if (this.IsOemMake) {
                        await this.GetModelsSmartEquip();
                    } else {
                        await this.GetCompetitiveModels();
                    }
                    
                    // Try to restore the model from the prop
                    if (this.model && this.models.length > 0) {
                        const matchedModel = this.models.find((m: any) => 
                            (m.ModelName === this.model) || 
                            (m.displayLabel === this.model)
                        );
                        if (matchedModel) {
                            this.selectedModel = matchedModel;
                        }
                    }
                    
                    // If we have a model selected, continue restoration
                    if (this.selectedModel) {
                        if (this.IsOemMake) {
                            // OEM flow: restore component group and component
                            await this.GetComponentGroups();
                            
                            if (this.componentGroup && this.componentGroups.length > 0) {
                                const matchedGroup = this.componentGroups.find((cg: any) => 
                                    cg.PublicationTitle === this.componentGroup
                                );
                                if (matchedGroup) {
                                    this.selectedComponentGroup = matchedGroup;
                                    await this.GetComponentsSmartEquip();
                                    
                                    // Try to find and select the component
                                    if (this.component && this.displayedComponents.length > 0) {
                                        const findComponent = (components: any[]): any => {
                                            for (const comp of components) {
                                                if (comp.componentDescription === this.component || comp.name === this.component) {
                                                    return comp;
                                                }
                                                if (comp.children && comp.children.length > 0) {
                                                    const found = findComponent(comp.children);
                                                    if (found) return found;
                                                }
                                            }
                                            return null;
                                        };
                                        
                                        const matchedComponent = findComponent(this.displayedComponents);
                                        if (matchedComponent) {
                                            this.selectedComponent = matchedComponent;
                                            // Re-trigger the search so the parent loads results
                                            this.GoToSmartEquipResults(matchedComponent);
                                        }
                                    }
                                }
                            }
                        } else {
                            // Competitive flow: restore category
                            await this.GetCategories();
                            
                            if (this.category && this.categories.length > 0) {
                                const matchedCategory = this.categories.find((cat: string) => cat === this.category);
                                if (matchedCategory) {
                                    this.selectedCategory = matchedCategory;
                                    // Re-trigger the search so the parent loads results
                                    this.OnCategorySelected();
                                }
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error in GetData:", error);
            } finally {
                this.loading = false;
                (this as any)._isRestoring = false;
            }
        },

        /** Fetches models for a competitive (non-OEM) make via TmsService. */
        async GetCompetitiveModels() {
            try {
                this.models = ["loading models..."];
                this.loadingModels = true;
                this.clearDependentSelections("model");
                this.focusSelect('modelSelectCompetitive');

                const facets = { OemDescription: this.selectedMake.OemDescription };
                const searchInput = this.CreateSearchInput(facets);
                const response = await TmsService.GetModels(searchInput);
                this.models = response?.data ?? [];

                if (this.models.length === 1) {
                    this.selectedModel = this.models[0];
                    await this.GetCategories();
                }
            } catch (error) {
                console.error("Error loading models:", error);
                this.models = [];
            } finally {
                this.loadingModels = false;
                this.focusSelect('modelSelectCompetitive');
            }
        },

        /** Fetches models for an OEM make via SmartEquipService. */
        async GetModelsSmartEquip() {
            try {
                this.loadingModels = true;
                this.clearDependentSelections("model");
                this.focusSelect('modelSelect');

                if (!this.selectedMake?.BrandId) return;

                const response = await SmartEquipService.GetModels(
                    this.user.UserId,
                    this.selectedMake.BrandId,
                    null //no truck class
                );

                if (response.error) throw new Error(response.error.Message);

                this.models = (response.Products || []).map((m: any) => {
                    const serial = (m.SerialNumberStart || m.SerialNumberEnd)
                        ? ` (${m.SerialNumberStart || '?'} – ${m.SerialNumberEnd || '?'})`
                        : '';
                    return { ...m, displayLabel: `${m.ModelName}${serial}` };
                });

                if (this.models.length === 1) {
                    this.selectedModel = this.models[0];
                    await this.GetComponentGroups();
                }
            } catch (error) {
                console.error("Error loading models:", error);
                this.models = [];
            } finally {
                this.loadingModels = false;
                this.focusSelect('modelSelect');
            }
        },

        /** Fetches component groups for the selected OEM model via SmartEquipService. */
        async GetComponentGroups() {
            try {
                this.loadingComponentGroups = true;
                this.clearDependentSelections("componentGroup");
                this.focusSelect('componentGroupSelect');
                if (!this.selectedMake?.BrandId || !this.selectedModel?.ModelIdAndSerialNumberRangeId) return;

                const response = await SmartEquipService.GetComponentGroups(
                    this.user.UserId,
                    this.selectedMake.BrandId,
                    this.selectedModel.ModelIdAndSerialNumberRangeId
                );

                if (response.error) throw new Error(response.error.Message);

                this.componentGroups = response.ComponentGroups || [];

                if (this.componentGroups.length === 1) {
                    this.selectedComponentGroup = this.componentGroups[0];
                    await this.GetComponentsSmartEquip();
                }
            } catch (error) {
                console.error("Error loading component groups:", error);
                this.componentGroups = [];
            } finally {
                this.loadingComponentGroups = false;
                this.focusSelect('componentGroupSelect');
            }
        },

        /** Fetches leaf components within a component group via SmartEquipService. */
        async GetComponentsSmartEquip() {
            try {
                this.$emit("selection-reset");
                this.loadingComponents = true;
                this.selectedComponent = null;
                this.displayedComponents = [];

                if (!this.selectedMake?.BrandId || !this.selectedComponentGroup?.PublicationIdAndDataSource) return;

                const response = await SmartEquipService.GetComponents(
                    this.user.UserId,
                    this.selectedMake.BrandId,
                    this.selectedComponentGroup.PublicationIdAndDataSource
                );

                if (response.error) throw new Error(response.error.Message);

                // Recursively transform the API response into the display structure
                const transformComponent = (comp: any): any => ({
                    id: comp.PublicationIdAndDataSource,
                    name: comp.PublicationTitle,
                    componentDescription: comp.PublicationTitle,
                    hasChildren: comp.HasChildComponents,
                    children: comp.ChildComponents ? comp.ChildComponents.map(transformComponent) : []
                });

                this.displayedComponents = (response.Components || []).map(transformComponent);
            } catch (error) {
                console.error("Error loading components:", error);
                this.displayedComponents = [];
            } finally {
                this.loadingComponents = false;
                this.focusSelect('componentGroupSelect');
            }
        },

        /** Fetches categories for the selected competitive make + model. */
        async GetCategories() {
            try {
                this.loadingCategories = true;
                this.selectedCategory = "";
                this.categories = ["loading categories..."];
                this.focusSelect('categorySelect');
                const facets = {
                    OemDescription: this.selectedMake.OemDescription,
                    Model: this.selectedModel
                };
                const searchInput = this.CreateSearchInput(facets);
                this.categories = (await TmsService.GetCategories(searchInput)).data;
            } catch (error) {
                console.error("Error in GetCategories:", error);
                this.categories = [];
            } finally {
                this.loadingCategories = false;
                this.focusSelect('categorySelect');
            }
        },

        // ─── Selection handlers ────────────────────────────────────────

        /** Handles model selection – loads component groups (OEM) or categories (competitive). */
        async OnModelSelected() {
            if (!this.selectedModel) return;
            try {
                this.$emit("selection-reset");
                if (this.IsOemMake && this.showDiagram) {
                    await this.GetComponentGroups();
                } else {
                    await this.GetCategories();
                }
                this.updateQueryString();
            } catch (error) {
                console.error("Error in OnModelSelected:", error);
            }
        },

        /** Handles category selection for competitive makes. */
        async OnCategorySelected() {
            if (!this.selectedCategory) return;
            try {
                this.updateQueryString();
                const queryData = {
                    OemDescription: this.selectedMake.OemDescription, 
                    Model: this.selectedModel, 
                    Category: this.selectedCategory,
                    showDiagram: false
                };
                if (this.embedded) {
                    this.$emit("search-complete", queryData);
                } else {
                    this.$router.push({ name: "TruckModelSearch", query: queryData });
                }
            } catch (error) {
                console.error("Error in OnCategorySelected:", error);
            }
        },

        /** Handles component group selection – loads components and updates query. */
        async OnComponentGroupSelected() {
            if (!this.selectedComponentGroup) return;
            await this.GetComponentsSmartEquip();
            this.updateQueryString();
        },

        /** Selects a leaf component and navigates to results. */
        SelectComponent(component: any) {
            this.selectedComponent = component;
            if (!component.hasChildren) {
                if (this.showDiagram) {
                    this.GoToSmartEquipResults(component);
                } else {
                    this.GoToResultsPage();
                }
            }
        },

        /** Toggles a component's expanded/collapsed state; selects leaf nodes. */
        ToggleComponent(component: any) {
            if (component.hasChildren) {
                // Toggle expanded state for parent components
                const idx = this.expandedComponentIds.indexOf(component.id);
                if (idx >= 0) {
                    this.expandedComponentIds.splice(idx, 1);
                } else {
                    this.expandedComponentIds.push(component.id);
                }
            } else {
                // Leaf component: select and navigate
                this.selectedComponent = component;
                if (this.showDiagram) {
                    this.GoToSmartEquipResults(component);
                } else {
                    this.GoToResultsPage();
                }
            }
        },

        // ─── Navigation ────────────────────────────────────────────────

        /** Navigates to SmartEquip diagram results for the given component. */
        async GoToSmartEquipResults(component: any) {
            try {
                this.updateQueryString();

                // Drill down to the deepest leaf component
                let leafComponent = component;
                while (leafComponent.hasChildren && leafComponent.children?.length > 0) {
                    leafComponent = leafComponent.children[0];
                }

                const queryData = {
                    userId: this.user.UserId,
                    brandId: this.selectedMake.BrandId,
                    componentNumber: leafComponent.id,
                    componentGroupId: this.selectedComponentGroup.PublicationIdAndDataSource,
                    oemDescription: this.selectedMake.OemDescription,
                    model: this.selectedModel.ModelName,
                    componentGroup: this.selectedComponentGroup.PublicationTitle,
                    component: leafComponent.name,
                    showDiagram: true
                };

                if (this.embedded) {
                    this.$emit("search-complete", queryData);
                } else {
                    this.$router.push({ name: "DiagramSearch", query: queryData as any });
                }
            } catch (error) {
                console.error("Error in GoToSmartEquipResults:", error);
            }
        },

        /** Navigates to the traditional results page for a selected component. */
        GoToResultsPage() {
            if (!this.selectedComponent) return;
            const queryData = {
                OemDescription: this.selectedMake.OemDescription, 
                Model: this.selectedModel, 
                Component: this.selectedComponent.id,
                ComponentName: this.selectedComponent.componentDescription || this.selectedComponent.name
            };
            if (this.embedded) {
                this.$emit("search-complete", queryData);
            } else {
                this.$router.push({ name: "DiagramSearch", query: queryData });
            }
        },

        // ─── Reset helpers ─────────────────────────────────────────────

        /**
         * Clears dependent selections and option arrays from a given level
         * downward. Use this when an upstream selection changes to cascade-reset
         * all downstream choices.
         *
         * @param fromLevel - The level to start clearing from:
         *   "model" | "componentGroup" | "component" | "category"
         */
        clearDependentSelections(fromLevel: string) {
            const levels = ["model", "componentGroup", "component", "category"];
            const startIdx = levels.indexOf(fromLevel);
            if (startIdx < 0) return;

            const clearFrom = levels.slice(startIdx);

            if (clearFrom.includes("model")) {
                this.selectedModel = "";
                this.models = ["loading models..."];
            }
            if (clearFrom.includes("componentGroup")) {
                this.selectedComponentGroup = "";
                this.componentGroups = [];
            }
            if (clearFrom.includes("component")) {
                this.selectedComponent = null;
                this.displayedComponents = [];
                this.expandedComponentIds = [];
            }
            if (clearFrom.includes("category")) {
                this.selectedCategory = "";
                this.categories = [];
            }
        },

        /**
         * Scrolls a given element into view on the next tick.
         * Used by breadcrumb reset actions.
         */
        scrollToElement(selector: string) {
            this.$nextTick(() => {
                const el = document.querySelector(selector);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        },

        /** Reset everything back to the make selector. */
        ResetFromMake() {
            this.selectedMake = "";
            this.clearDependentSelections("model");
            this.showDiagram = false;
            this.updateQueryString();
            this.scrollToElement("#makeSelect");
        },

        /** Reset from model selection downward — keeps models list intact. */
        ResetFromModel() {
            this.selectedModel = null;
            this.selectedComponentGroup = null;
            this.componentGroups = [];
            this.selectedComponent = null;
            this.displayedComponents = [];
            this.expandedComponentIds = [];
            this.selectedCategory = null;
            this.categories = [];
            this.$emit("selection-reset");
            this.updateQueryString();
            this.scrollToElement("#modelSelect");
        },

        /** Reset category selection. */
        ResetFromCategory() {
            this.selectedCategory = null;
            this.$emit("selection-reset");
            this.updateQueryString();
            this.scrollToElement("#categorySelect");
        },

        /** Reset from component group selection downward — keeps componentGroups list intact. */
        ResetFromComponentGroup() {
            this.selectedComponentGroup = null;
            this.selectedComponent = null;
            this.displayedComponents = [];
            this.expandedComponentIds = [];
            this.$emit("selection-reset");
            this.updateQueryString();
            this.scrollToElement("#componentGroupSelect");
        },

        /** Reset component selection only. */
        ResetFromComponent() {
            this.selectedComponent = null;
            this.expandedComponentIds = [];
            this.$emit("selection-reset");
            this.updateQueryString();
            this.scrollToElement(".component-list");
        },

        // ─── Utilities ─────────────────────────────────────────────────

        /**
         * Updates the browser's query string to reflect the current selections.
         * Uses history.replaceState instead of $router.replace to avoid
         * triggering a component re-create from <router-view :key="$route.fullPath">.
         */
        updateQueryString() {
            if (!this.embedded) return; // Only update query string in embedded mode
            
            const query: Record<string, string> = {};
            
            // Update or remove make
            if (this.selectedMake) {
                query.OemDescription = this.selectedMake.OemDescription || this.selectedMake;
            }
            
            // Update or remove model
            if (this.selectedModel) {
                query.Model = this.selectedModel.ModelName || this.selectedModel;
            }
            
            // Update or remove category (competitive flow)
            if (this.selectedCategory) {
                query.Category = this.selectedCategory;
            }
            
            // Update or remove component group (OEM flow)
            if (this.selectedComponentGroup) {
                query.ComponentGroup = this.selectedComponentGroup.PublicationTitle || this.selectedComponentGroup;
            }
            
            // Update or remove component (OEM flow)
            if (this.selectedComponent) {
                query.Component = this.selectedComponent.componentDescription || this.selectedComponent.name || this.selectedComponent;
            }
            
            // Build the new URL with query params
            const params = new URLSearchParams(query);
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            
            // Only update if changed — uses history API directly to avoid
            // Vue Router's reactivity and the router-view :key re-render
            if (newUrl !== `${window.location.pathname}${window.location.search}`) {
                history.replaceState(history.state, '', newUrl);
            }
        },
        
        /** Builds a TMS search-input object, optionally including facets. */
        CreateSearchInput(facets?: object) {
            const searchInput: Record<string, any> = {
                AccountId: this.activeAccountId,
                UserId: this.user.UserId,
                DealerCode: this.user.DealerCode,
                Audiences: this.audienceIds
            };
            if (facets) {
                searchInput.Facets = facets;
            }
            return searchInput;
        },

        /**
         * Handles Tab keydown on a v-select — makes Tab behave exactly like
         * Enter, but only when the dropdown is open with options available.
         * If the dropdown is closed (already selected) or has no options
         * (still loading), we let the browser's default Tab behavior through
         * so focus moves forward naturally.
         */
        handleTab(event: KeyboardEvent, refName: string) {
            const vSelectRef = this.$refs[refName] as any;
            if (vSelectRef && vSelectRef.open && vSelectRef.filteredOptions && vSelectRef.filteredOptions.length > 0) {
                event.preventDefault();
                vSelectRef.typeAheadSelect();
            }
            // Otherwise: don't preventDefault — browser Tab moves focus normally
        },

        /**
         * Programmatically focuses a v-select by ref name after the DOM has
         * settled.  Skips focus during state restoration (GetData) and only
         * focuses when the target v-select is enabled and actually has
         * options to display (not still loading with an empty list).
         */
        focusSelect(refName: string) {
            // Don't steal focus during deep-link restoration
            if ((this as any)._isRestoring) return;

            // Use a short delay so the v-select has time to render
            // with its new options after the reactive data update.
            this.$nextTick(() => {
                this.$nextTick(() => {
                    const vSelectRef = this.$refs[refName] as any;
                    if (vSelectRef
                        && !vSelectRef.disabled
                        && vSelectRef.filteredOptions
                        && vSelectRef.filteredOptions.length > 0) {
                        const searchInput = vSelectRef.$refs?.search
                            || vSelectRef.$el?.querySelector('input[type="search"]');
                        if (searchInput) {
                            searchInput.focus();
                        }
                    }
                });
            });
        },
    }
});